import { useState } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import {
  Popover,
  PopoverContent,
} from "@/components/ui/popover";
import { Tooltip, TooltipContent } from "@/components/ui/tooltip";

type BuildVersionBadgeProps = {
  humanVersion: string;
  commitSha?: string;
  builtAt?: string;
  environment?: string;
};

export function BuildVersionBadge({
  humanVersion,
  commitSha,
  builtAt,
  environment,
}: BuildVersionBadgeProps) {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [detailsPopoverOpen, setDetailsPopoverOpen] = useState(false);
  const hasExactSha = /^[0-9a-f]{40}$/i.test(commitSha ?? "");
  const shortSha = hasExactSha ? commitSha!.slice(0, 7) : undefined;
  const details = [
    hasExactSha ? `SHA completo: ${commitSha}` : undefined,
    builtAt ? `Build: ${builtAt}` : undefined,
    environment ? `Entorno: ${environment}` : undefined,
  ].filter(Boolean).join(" · ");

  const label = `Versión ${humanVersion}${shortSha ? ` · ${shortSha}` : ""}${details ? `. ${details}` : ""}`;

  if (!details) {
    return (
      <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-bold ml-1">
        Versión {humanVersion}{shortSha ? ` · ${shortSha}` : ""}
      </span>
    );
  }

  return (
    <Popover open={detailsPopoverOpen} onOpenChange={setDetailsPopoverOpen}>
      <Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
        <PopoverPrimitive.Trigger asChild>
          <TooltipPrimitive.Trigger asChild>
            <button
              type="button"
              aria-label={label}
              className="group relative inline-flex align-middle rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700 ml-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600 focus-visible:ring-offset-1"
            >
              Versión {humanVersion}{shortSha ? ` · ${shortSha}` : ""}
            </button>
          </TooltipPrimitive.Trigger>
        </PopoverPrimitive.Trigger>
        <TooltipContent
          side="bottom"
          className="max-w-[calc(100vw-2rem)] break-all text-xs"
        >
          {details}
        </TooltipContent>
      </Tooltip>
      <PopoverContent
        side="bottom"
        align="start"
        aria-label="Detalles de versión"
        className="max-h-[70vh] max-w-[calc(100vw-2rem)] overflow-y-auto break-all p-3 text-xs"
      >
        {details}
      </PopoverContent>
    </Popover>
  );
}
